import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    Directive,
    Input,
    NgZone,
    OnDestroy,
    PLATFORM_ID,
    inject,
} from '@angular/core';

@Directive({
    selector: '[widgetMotorMoveToHeader]',
    standalone: true,
})
export class WidgetMotorMoveToHeaderDirective
    implements AfterViewInit, OnDestroy
{
    private readonly platformId = inject(PLATFORM_ID);
    private readonly zone = inject(NgZone);

    /** Nodo real del motor que quieres mover (ej: '#widget-search') */
    @Input('widgetMotorMoveToHeader') motorSelector!: string;

    /** Slot fijo en el header donde VIVIRÁ el motor */
    @Input() headerSlotSelector = '.mdl-header .m-motor';

    /** Clase que se añade al motor cuando ya está arriba */
    @Input() movedClass = 'motor-in-header';

    /** Anti-flash: oculto hasta que el header slot esté listo */
    @Input() pendingClass = 'motor-pending';
    @Input() readyClass = 'motor-ready';

    private obs?: MutationObserver;
    private rafId?: number;
    private moved = false;
    private ready = false;

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        if (!this.motorSelector) return;

        this.zone.runOutsideAngular(() => {
            // Oculta cuanto antes si el motor ya existe
            this.markPendingIfExists();

            // intenta ya
            this.tryMove();

            // observa el DOM hasta conseguirlo (widgets tardíos / navegación)
            this.obs = new MutationObserver(() => {
                this.markPendingIfExists();
                this.tryMove();
            });
            this.obs.observe(document.body, { childList: true, subtree: true });
        });
    }

    private markPendingIfExists() {
        if (this.moved) return;
        const motor = document.querySelector(
            this.motorSelector
        ) as HTMLElement | null;
        if (!motor) return;

        document.body.classList.add('widget-search-pending');
        document.body.classList.remove('widget-search-ready');
        motor.classList.add(this.pendingClass);
        motor.classList.remove(this.readyClass);
    }

    private markReady(motor: HTMLElement) {
        motor.classList.remove(this.pendingClass);
        motor.classList.add(this.readyClass);
    }

    private isWidgetReady(motor: HTMLElement): boolean {
        return (
            motor.querySelector(
                '.widget-search-container, .widget-search, .search-button'
            ) !== null
        );
    }

    private tryMove() {
        if (this.ready) return;

        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => {
            const motor = document.querySelector(
                this.motorSelector
            ) as HTMLElement | null;
            const slot = document.querySelector(
                this.headerSlotSelector
            ) as HTMLElement | null;

            // Si no hay motor aún, nada. Si no hay slot aún, seguimos esperando (y el motor queda oculto).
            if (!motor || !slot) return;

            // ✅ mover UNA sola vez al header
            if (!slot.contains(motor)) {
                slot.appendChild(motor);
                this.moved = true;
            }

            motor.classList.add(this.movedClass);

            if (!this.isWidgetReady(motor)) {
                return;
            }

            this.markReady(motor);
            document.body.classList.remove('widget-search-pending');
            document.body.classList.add('widget-search-ready');

            this.ready = true;
            this.obs?.disconnect();
        });
    }

    ngOnDestroy(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        this.obs?.disconnect();
        if (this.rafId) cancelAnimationFrame(this.rafId);
        document.body.classList.remove('widget-search-pending');
    }
}
